import { SaCard, SA } from "./SuperAdminUi";

export default function IntegrationGuidePanel({
  icon,
  iconColor,
  brand,
  title,
  description,
  bullets,
  configNote,
}) {
  return (
    <SaCard style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "flex-start" }}>
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "14px",
            background: `${iconColor ?? SA.primary}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "26px",
            color: iconColor ?? SA.primary,
            flexShrink: 0,
          }}
        >
          <i className={icon} />
        </div>
        <div style={{ flex: "1 1 260px" }}>
          <p
            style={{
              margin: "0 0 4px",
              fontSize: "12px",
              fontWeight: "800",
              color: iconColor ?? SA.primary,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {brand}
          </p>
          <h3 style={{ margin: "0 0 10px", fontSize: "17px", fontWeight: "800", color: SA.ink }}>{title}</h3>
          <p style={{ margin: "0 0 14px", fontSize: "14px", color: SA.muted, lineHeight: 1.6 }}>{description}</p>
          <ul style={{ margin: 0, paddingLeft: "18px", color: SA.ink, fontSize: "13px", lineHeight: 1.8 }}>
            {bullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          {configNote && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px 16px",
                borderRadius: "10px",
                background: `${iconColor ?? SA.primary}10`,
                border: `1px solid ${iconColor ?? SA.primary}30`,
                fontSize: "13px",
                color: SA.ink,
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <i className="fas fa-info-circle" style={{ color: iconColor ?? SA.primary, marginTop: "2px" }} />
              <span>{configNote}</span>
            </div>
          )}
        </div>
      </div>
    </SaCard>
  );
}

export const INTEGRATION_GUIDES = [
  {
    key: "facebook",
    icon: "fab fa-facebook",
    iconColor: "#1877F2",
    brand: "Meta",
    title: "Facebook Lead Ads",
    description:
      "Connect a Meta app with Lead Ads permissions to pull leads into the CRM. Each agency stores its own App ID, App Secret, Access Token, and Lead Gen form ID.",
    bullets: [
      "Configure credentials in the Facebook Ads tab above.",
      "Rotate access tokens periodically; use long-lived tokens via token exchange.",
      "Webhook URL must match your deployed API host.",
      "Ensure your Meta App has leads_retrieval and pages_read_engagement permissions.",
    ],
    configNote: "Use the Facebook Ads tab to save this agency's credentials.",
  },
  {
    key: "google",
    icon: "fab fa-google",
    iconColor: "#4285F4",
    brand: "Google",
    title: "Google Ads Lead Form Extensions",
    description:
      "Pull leads from Google Ads Lead Form Extensions using the Google Ads API (OAuth 2.0 + Developer Token).",
    bullets: [
      "Requires a Google Ads Developer Token (approved for Basic or Standard access).",
      "Create OAuth 2.0 credentials in Google Cloud Console (Web Application type).",
      "Run the OAuth consent flow once to obtain a Refresh Token with the ads scope.",
      "For Manager (MCC) accounts, provide both the MCC Customer ID and sub-account Customer ID.",
    ],
    configNote: "Use the Google Ads tab to save this agency's credentials.",
  },
  {
    key: "linkedin",
    icon: "fab fa-linkedin",
    iconColor: "#0A66C2",
    brand: "LinkedIn",
    title: "LinkedIn Lead Gen Forms",
    description:
      "Capture leads from LinkedIn Lead Gen Forms via the LinkedIn Marketing API.",
    bullets: [
      "Create an app in the LinkedIn Developer Portal with the Marketing Developer Platform product.",
      "Request rw_lead_gen_forms (or r_liteprofile + rw_leads) OAuth 2.0 scopes.",
      "Generate an Access Token using the 3-legged OAuth flow.",
      "Provide your LinkedIn Ad Account ID (URN) for lead retrieval.",
    ],
    configNote: "Use the LinkedIn tab to save this agency's credentials.",
  },
  {
    key: "instagram",
    icon: "fab fa-instagram",
    iconColor: "#C13584",
    brand: "Meta / Instagram",
    title: "Instagram Lead Ads (Graph API)",
    description:
      "Pull leads from Instagram Lead Ad campaigns via the Meta Graph API.",
    bullets: [
      "Link the Instagram Business account to a Facebook Page in Meta Business Suite.",
      "Your Meta App needs leads_retrieval, pages_read_engagement, and instagram_basic permissions.",
      "Generate a long-lived Page Access Token with the required scopes.",
      "Locate the Instagram Business Account ID under Settings → Professional Account.",
    ],
    configNote: "Use the Instagram tab to save this agency's credentials.",
  },
  {
    key: "99acres",
    icon: "fas fa-city",
    iconColor: "#E84545",
    brand: "99acres",
    title: "99acres XML / portal leads",
    description:
      "Receive enquiries from 99acres via XML push or pull API.",
    bullets: [
      "Obtain the API key from the 99acres partner portal under Integrations.",
      "Map incoming XML fields (name, phone, email, project) to the CRM lead schema.",
      "Set a listing ID to route enquiries to the correct property project.",
      "Monitor backend logs for rejected or malformed XML payloads.",
    ],
    configNote: "Use the 99acres tab to save this agency's credentials.",
  },
  {
    key: "messaging",
    icon: "fas fa-comment-dots",
    iconColor: "#7B61FF",
    brand: "Twilio",
    title: "SMS & WhatsApp outbound messaging",
    description:
      "Each agency can enable Twilio-backed SMS and WhatsApp. Messages are sent only when the integration is enabled and the relevant channel toggle is on.",
    bullets: [
      "Provide Twilio Account SID, Auth Token, SMS From (E.164), and WhatsApp From (whatsapp:+…).",
      "Backend uses IMessagingService with clientId when workflows trigger sends.",
      "Auth tokens are never returned after save — leave blank on update to keep the existing secret.",
    ],
    configNote: "Use the SMS & WhatsApp tab to configure Twilio for this agency.",
  },
];
